import {Field as FieldPrimitive} from "@base-ui/react/field"
import {clsx} from "clsx"
import styles from "./Field.module.css"
import inputStyles from "./Input.module.css"

function FieldRoot({className, ...props}: FieldPrimitive.Root.Props) {
	return (
		<FieldPrimitive.Root
			data-slot="field"
			className={clsx(styles.root, className)}
			{...props}
		/>
	)
}

/**
 * Label automatically associated with the field's control — no `htmlFor`/`id`
 * plumbing needed. Styled from the CSS module (control-label look, via the token
 * bridge); goes red when the field is invalid (Base UI `data-invalid`). Two
 * grouped-control variants are handled by the module so the call site needs no
 * extra markup:
 * - **Group heading**: `render={<legend />} nativeLabel={false}` inside a
 *   `Field.Root render={<fieldset />}` around a Radio/CheckboxGroup. Renders a
 *   native `<legend>` that looks like a stacked label (and goes red on error).
 * - **Single checkbox/radio field**: enclose the control and its text
 *   (`<Field.Label><Checkbox />Accept…</Field.Label>`). Lays out as an inline row;
 *   the text is the field's label, so it goes red on error.
 */
function FieldLabel({className, ...props}: FieldPrimitive.Label.Props) {
	return (
		<FieldPrimitive.Label
			data-slot="field-label"
			className={clsx(styles.label, className)}
			{...props}
		/>
	)
}

/**
 * Optional native control, rendered as a token-styled `<input>` (same look as the
 * `Input` primitive). You can omit this and drop any Base UI input primitive
 * (`Input`, `Checkbox`, `RadioGroup`, …) inside `Field` instead — they hook into
 * the field's label/validation automatically. Forwards `ref` (React 19 ref-as-prop)
 * so form libraries can focus/register the underlying input.
 */
function FieldControl({className, ...props}: FieldPrimitive.Control.Props) {
	return (
		<FieldPrimitive.Control
			data-slot="field-control"
			className={clsx(inputStyles.root, className)}
			{...props}
		/>
	)
}

/**
 * Muted helper text describing the field. Renders a `<p>`, auto-linked into the
 * control's `aria-describedby`. Stays grey when the field is invalid (only the
 * error message goes red) — hence a CSS module, not Bootstrap `.help-block`.
 */
function FieldDescription({className, ...props}: FieldPrimitive.Description.Props) {
	return (
		<FieldPrimitive.Description
			data-slot="field-description"
			className={clsx(styles.description, className)}
			{...props}
		/>
	)
}

/**
 * Validation message shown only while the field is invalid, auto-linked into the
 * control's `aria-describedby`. With no children it renders the control's native
 * validity message; `match="valueMissing"` targets a specific `ValidityState`
 * key; `match={boolean}` lets an external library control visibility.
 */
function FieldError({className, ...props}: FieldPrimitive.Error.Props) {
	return (
		<div className={styles.slot}>
			<FieldPrimitive.Error
				data-slot="field-error"
				className={clsx(styles.error, className)}
				{...props}
			/>
		</div>
	)
}

/**
 * Groups a single option (with its own label/description) inside a checkbox or
 * radio group, giving that option its own field context. Behaviour only — it
 * adds no layout of its own, so arrange the control and label at the call site.
 * Use only when an option needs its own label/description; a group's single
 * error belongs on the surrounding `Field` (one `name`, one `FieldError`).
 */
function FieldItem({className, ...props}: FieldPrimitive.Item.Props) {
	return <FieldPrimitive.Item data-slot="field-item" className={className} {...props} />
}


// Render-prop escape hatch exposing the field's live `ValidityState` for fully custom messaging. Headless — renders no element of its own.


const Field = {
	Root: FieldRoot, // "Root" oder "Field" – je nachdem, was dir besser gefällt
	Label: FieldLabel,
	Control: FieldControl,
	Description: FieldDescription,
	Error: FieldError,
	Item: FieldItem,
	Validity: FieldPrimitive.Validity, // Render-prop escape hatch exposing the field's live `ValidityState` for fully custom messaging. Headless — renders no element of its own.
};

export {Field};
